import { useEffect, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { api, fmtBytes } from "@/lib/api";
import { useFiles } from "@/context/FilesContext";
import { AdminUsers } from "@/components/admin/AdminUsers";
import { AdminAudit } from "@/components/admin/AdminAudit";
import { AdminAlerts } from "@/components/admin/AdminAlerts";
import { AdminBackups } from "@/components/admin/AdminBackups";
import { AdminFolderMerge } from "@/components/admin/AdminFolderMerge";
import { AdminRgpd, ExportJobs } from "@/components/admin/AdminRgpd";
import { AdminStorage } from "@/components/admin/AdminStorage";
import { AdminCompanies } from "@/components/admin/AdminCompanies";
import { FileIcon } from "@/components/files/FileIcon";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const cards = [
    ["Utilisateurs actifs", `${s.active_users} / ${s.users}`, "admin-stat-users"],
    ["En attente de validation", s.pending_users, "admin-stat-pending"],
    ["Documents", s.files, "admin-stat-files"],
    ["Stockage", fmtBytes(s.storage_bytes), "admin-stat-storage"],
    ["Analysés par l'IA", s.ai_analyzed, "admin-stat-ai"],
    ["Liens publics actifs", s.active_links, "admin-stat-links"],
    ["Connexions (7 j)", s.logins_7d, "admin-stat-logins"],
    ["Actions (7 j)", s.actions_7d, "admin-stat-actions"],
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map(([l, v, id]) => (
        <div key={id} className="bg-white border rounded-xl p-5" data-testid={id}>
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{l}</div>
          <div className="font-display text-2xl font-extrabold mt-2">{v}</div>
        </div>
      ))}
    </div>
  );
}

function Duplicates() {
  const [groups, setGroups] = useState(null);
  const { openFile } = useFiles();
  useEffect(() => { api.get("/ai/duplicates").then((r) => setGroups(r.data)); }, []);
  if (!groups) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (!groups.length) return <div className="border border-dashed rounded-xl py-12 text-center text-sm text-slate-500" data-testid="duplicates-empty">Aucun doublon détecté.</div>;
  return (
    <div className="space-y-3" data-testid="duplicates-list">
      {groups.map((g, i) => (
        <div key={i} className="bg-white border rounded-xl p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-2">{g.reason}</div>
          {g.files.map((f) => (
            <button key={f.id} onClick={() => openFile(f.id)} className="w-full flex items-center gap-3 px-2 py-2 rounded hover:bg-slate-50 text-left">
              <FileIcon file={f} size="sm" /><span className="text-sm flex-1 truncate">{f.name}</span><span className="text-xs text-slate-500">{f.owner_name}</span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

function Compliance() {
  const rules = [
    ["Chiffrement", "HTTPS/TLS pour toutes les communications ; fichiers stockés dans un stockage objet chiffré au repos."],
    ["Contrôle d'accès", "Rôles (administrateur, responsable, salarié, externe) + autorisations par dossier et par fichier, vérifiées côté serveur à chaque requête ; mots de passe de synchronisation distincts par PC, révocables."],
    ["Journalisation", "Connexions, consultations, téléchargements, partages, changements de droits et exports sont tracés avec horodatage et IP."],
    ["Conservation", "Corbeille conservée 30 jours puis suppression définitive ; durée du journal d'audit paramétrable ci-dessous."],
    ["IA", "L'assistant ne reçoit que les documents auxquels l'utilisateur a accès ; aucune donnée n'est utilisée pour entraîner les modèles."],
    ["Droits des personnes", "Chaque utilisateur télécharge ses données (art. 15/20) ; l'administrateur peut exporter ou effacer un compte (art. 17) depuis l'onglet Utilisateurs."],
  ];
  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="bg-white border rounded-xl divide-y">
          {rules.map(([t, d]) => (
            <div key={t} className="p-4 flex gap-3"><ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
              <div><div className="font-semibold text-sm">{t}</div><p className="text-sm text-slate-600 mt-0.5">{d}</p></div></div>
          ))}
        </div>
        <ExportJobs />
      </div>
      <AdminRgpd />
    </div>
  );
}

export default function Admin() {
  return (
    <div className="max-w-7xl mx-auto fade-up" data-testid="admin-page">
      <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">Espace administrateur</p>
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1 mb-6">Administration</h1>
      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto mb-6">
          <TabsTrigger value="overview" data-testid="admin-tab-overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="companies" data-testid="admin-tab-companies">Sociétés</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Utilisateurs</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit">Journal d'audit</TabsTrigger>
          <TabsTrigger value="duplicates" data-testid="admin-tab-duplicates">Doublons</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="admin-tab-alerts">Paramètres</TabsTrigger>
          <TabsTrigger value="backups" data-testid="admin-tab-backups">Sauvegardes</TabsTrigger>
          <TabsTrigger value="storage" data-testid="admin-tab-storage">Stockage</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="admin-tab-compliance">Sécurité & RGPD</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><Overview /></TabsContent>
        <TabsContent value="storage"><AdminStorage /></TabsContent>
        <TabsContent value="companies"><AdminCompanies /></TabsContent>
        <TabsContent value="users"><AdminUsers /></TabsContent>
        <TabsContent value="audit"><AdminAudit /></TabsContent>
        <TabsContent value="duplicates"><div className="space-y-8"><AdminFolderMerge /><div><h3 className="font-display font-bold mb-3">Fichiers identiques</h3><Duplicates /></div></div></TabsContent>
        <TabsContent value="alerts"><AdminAlerts /></TabsContent>
        <TabsContent value="backups"><AdminBackups /></TabsContent>
        <TabsContent value="compliance"><Compliance /></TabsContent>
      </Tabs>
    </div>
  );
}
